// Таблицата на справката „Прехвърляния": ниски редове на един ред. Широк
// екран — № · Дата и час · Направление · Потребител · Коментар · Продукти ·
// Нето · Бруто; лаптоп/таблет — № · Дата и час · Направление (потребителят,
// продуктите и коментарът приглушено до него) · Бруто; телефон — карти.
// Филтрите са в заглавията: „Направление" носи От/Към/Статус (на средна
// ширина и Потребител). Заглавията сортират. Анулираните стоят зачертани, а
// редът „Общо" е без тях. „Печат" и менюто „⋯" са на реда. Клавиатура: ↑/↓
// по редовете, Enter отваря документа.
import type { ReactNode } from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import { fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { StatusPill } from "../../components/list/ListBits";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import {
  fromWarehouseOf,
  isTransferAnnulled,
  toWarehouseOf,
  transferUserOf,
} from "../../transfer/components/transferMeta";
import { ReportHead } from "../components/ReportListBits";
import {
  REPORT_TOTAL_LABEL_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type { TransferFilterKey, TransferSort, TransferSortKey } from "./transfersReportModel";

export type TransfersDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[56px_128px_minmax(190px,1.4fr)_minmax(100px,.9fr)_minmax(120px,1fr)_76px_96px_104px_100px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[52px_128px_minmax(200px,1fr)_104px_100px] items-center gap-2.5 px-[18px]",
} as const;

type Props = {
  rows: TransferDocument[];
  density: TransfersDensity;
  sort: TransferSort;
  onSort: (key: TransferSortKey) => void;
  filters: ColumnSections<TransferFilterKey>;
  totalNet: number;
  totalGross: number;
  onOpen: (transfer: TransferDocument) => void;
  onPrint: (transfer: TransferDocument) => void;
  actionsOf: (transfer: TransferDocument) => RowAction[];
  footer?: ReactNode;
};

export const TransfersReportTable = ({
  rows,
  density,
  sort,
  onSort,
  filters,
  totalNet,
  totalGross,
  onOpen,
  onPrint,
  actionsOf,
  footer,
}: Props) => {
  const { t } = useTranslation();

  const numberClass = (transfer: TransferDocument) =>
    isTransferAnnulled(transfer) ? "text-faint line-through" : "text-ink";
  const annulledPill = (transfer: TransferDocument) =>
    isTransferAnnulled(transfer) && (
      <StatusPill tone="neutral">{t("inventoryPage.statusAnnulled")}</StatusPill>
    );
  const direction = (transfer: TransferDocument) => (
    <span className={`flex min-w-0 shrink items-center gap-2 ${numberClass(transfer)}`}>
      <span className="truncate">{fromWarehouseOf(transfer)}</span>
      <ArrowRightOutlined className="shrink-0 text-[11px] text-brand-deep-ink" />
      <span className="truncate">{toWarehouseOf(transfer)}</span>
    </span>
  );

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((transfer) => (
          <div
            key={transfer.id}
            {...openableRowProps(() => onOpen(transfer))}
            className="flex cursor-pointer flex-col gap-1.5 border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex items-center gap-2">
              <span className="font-sheet font-semibold text-ink">№{transfer.id}</span>
              <span className="min-w-0 flex-1 font-medium">{direction(transfer)}</span>
              {annulledPill(transfer)}
              <RowActionsMenu actions={actionsOf(transfer)} compact />
            </div>
            <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
              <span className="min-w-0 truncate">
                {fmtDateTime(transfer.date)} · {transferUserOf(transfer)} ·{" "}
                {t("inventoryPage.homeDraftLines", { count: transfer.productsCount ?? 0 })}
              </span>
              <span className={`shrink-0 font-sheet text-sm font-semibold ${numberClass(transfer)}`}>
                {formatMoney(transfer.totalValueWithVat || 0)}
              </span>
            </div>
          </div>
        ))}
        {footer}
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: TransferSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1090px]" : "min-w-[664px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("№")}
          {head(t("inventoryPage.colDateTime"), { sortKey: "date" })}
          {head(t("inventoryPage.trxDirection"), {
            sortKey: "direction",
            filters: full
              ? sectionList(filters.from, filters.to, filters.statuses)
              : sectionList(filters.from, filters.to, filters.users, filters.statuses),
          })}
          {full && (
            <>
              {head(t("inventoryPage.colUser"), { sortKey: "user", filters: sectionList(filters.users) })}
              {head(t("inventoryPage.colComment"), { sortKey: "note" })}
              {head(t("inventoryPage.colProducts"), { sortKey: "products", align: "right" })}
              {head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
            </>
          )}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right" })}
          <span />
        </div>
        {rows.map((transfer) => (
          <div
            key={transfer.id}
            {...openableRowProps(() => onOpen(transfer))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet font-semibold text-ink">№{transfer.id}</span>
            <span className="whitespace-nowrap font-sheet text-ink-2">{fmtDateTime(transfer.date)}</span>
            <span className="flex min-w-0 items-center gap-2 font-medium">
              {direction(transfer)}
              {annulledPill(transfer)}
              {!full && (
                <span className="min-w-0 flex-1 truncate font-normal text-muted">
                  · {transferUserOf(transfer)} ·{" "}
                  {t("inventoryPage.homeDraftLines", { count: transfer.productsCount ?? 0 })}
                  {transfer.note ? ` · ${transfer.note}` : ""}
                </span>
              )}
            </span>
            {full && (
              <>
                <span className="truncate font-medium text-ink">{transferUserOf(transfer)}</span>
                <span className="truncate text-ink-2" title={transfer.note ?? undefined}>
                  {transfer.note || <span className="text-faint">—</span>}
                </span>
                <span className="text-right font-sheet text-ink-2">{transfer.productsCount ?? 0}</span>
                <span className={`text-right font-sheet ${numberClass(transfer)}`}>
                  {formatMoney(transfer.totalValueNoVat || 0)}
                </span>
              </>
            )}
            <span className={`text-right font-sheet font-semibold ${numberClass(transfer)}`}>
              {formatMoney(transfer.totalValueWithVat || 0)}
            </span>
            <RowActionsMenu actions={actionsOf(transfer)} onPrint={() => onPrint(transfer)} dense />
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_TOTAL_LABEL_CLASS} ${full ? "col-span-6" : "col-span-3"}`}>
              {t("inventoryPage.periodTotalActive")}
            </span>
            {full && (
              <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totalNet)}</span>
            )}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">
              {formatMoney(totalGross)}
            </span>
            <span />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransfersReportTable;
